import { defineStore } from 'pinia';
import {
  CalculationMode,
  FireScenario,
  PlanService,
  ScenarioEvent,
  type CalculationMode as CalculationModeType,
  type FireScenario as FireScenarioType,
  type ScenarioEvent as ScenarioEventType,
  type ScenarioEventInit as ScenarioEventInitType,
  type MethodResult,
  type PlanResult,
} from '@fire-calc/domain-ts';

/**
 * Data shape accepted by CRUD actions. Partial — caller provides only
 * the fields they want to set; missing fields use entity defaults.
 */
type EventCreateData = Omit<ScenarioEventInitType, 'id'> & {
  id: string;
};

type EventUpdateData = Partial<ScenarioEventInitType>;

export type Phase = 'initial' | 'ready' | 'error';

export const CALCULATOR_DEFAULT_VALIDATION_DELAY_MS = 500;

export const SCENARIO_STORAGE_KEY = 'fire_calc:scenarios';

function makeDefaultScenario(): FireScenarioType {
  return FireScenario.create({
    id: typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `default-${Date.now()}`,
    name: 'Výchozí',
    monthlyExpenses: 0,
    currentAge: 0,
    retirementAge: 0,
  });
}

export const useCalculatorStore = defineStore('calculator', {
  state: () => ({
    scenario: makeDefaultScenario() as FireScenarioType,
    mode: CalculationMode.byRetirementAge as CalculationModeType,
    phase: 'initial' as Phase,
    planResult: null as PlanResult | null,
    monthlyExpensesError: null as string | null,
    currentAgeError: null as string | null,
    retirementAgeError: null as string | null,
    monthlyContributionError: null as string | null,
    touched: new Set<string>() as Set<string>,
    validationTimer: null as ReturnType<typeof setTimeout> | null,
  }),

  getters: {
    classical(state): MethodResult | undefined {
      return state.planResult?.classicalFire;
    },
    dwz(state): MethodResult | undefined {
      return state.planResult?.dieWithZero;
    },
    isReady(state): boolean {
      return state.phase === 'ready' && state.planResult !== null;
    },
  },

  actions: {
    touch(field: string): void {
      this.touched.add(field);
    },

    setMode(mode: CalculationModeType): void {
      if (this.mode === mode) return;
      this.mode = mode;
      this.retirementAgeError = null;
      this.monthlyContributionError = null;
      this.recompute();
    },

    updateMonthlyExpenses(value: number): void {
      this.touch('monthlyExpenses');
      this.scenario = this.scenario.copyWith({ monthlyExpenses: value });
      this.scheduleValidation();
    },

    updateCurrentAge(value: number): void {
      this.touch('currentAge');
      this.scenario = this.scenario.copyWith({ currentAge: value });
      this.scheduleValidation();
    },

    updateRetirementAge(value: number): void {
      this.touch('retirementAge');
      this.scenario = this.scenario.copyWith({ retirementAge: value });
      this.scheduleValidation();
    },

    updateMonthlyContribution(value: number): void {
      this.touch('monthlyContribution');
      this.scenario = this.scenario.copyWith({ monthlyContribution: value });
      this.scheduleValidation();
    },

    updateCurrentPortfolio(value: number): void {
      this.scenario = this.scenario.copyWith({ currentPortfolio: value });
      this.scheduleValidation();
    },

    updateAnnualReturn(value: number): void {
      this.scenario = this.scenario.copyWith({ annualReturn: value });
      this.scheduleValidation();
    },

    updateInflation(value: number): void {
      this.scenario = this.scenario.copyWith({ inflation: value });
      this.scheduleValidation();
    },

    updateSwr(value: number): void {
      this.scenario = this.scenario.copyWith({ swr: value });
      this.scheduleValidation();
    },

    toggleContributionGrowth(enabled: boolean): void {
      this.scenario = this.scenario.copyWith({ contributionGrowthEnabled: enabled });
      this.scheduleValidation();
    },

    loadScenario(scenario: FireScenarioType): void {
      this.scenario = scenario;
      this.touched = new Set();
      this.phase = 'ready';
      this.planResult = new PlanService().runFullPlan(this.scenario, this.mode);
    },

    reset(): void {
      this.scenario = makeDefaultScenario();
      this.mode = CalculationMode.byRetirementAge;
      this.phase = 'initial';
      this.planResult = null;
      this.monthlyExpensesError = null;
      this.currentAgeError = null;
      this.retirementAgeError = null;
      this.monthlyContributionError = null;
      this.touched = new Set();
    },

    computeErrors(): {
      monthlyExpenses: string;
      currentAge: string;
      retirementAge: string;
      monthlyContribution: string;
    } {
      const errors = {
        monthlyExpenses: '',
        currentAge: '',
        retirementAge: '',
        monthlyContribution: '',
      };
      if (this.scenario.monthlyExpenses.value <= 0) {
        errors.monthlyExpenses = 'Zadejte platnou částku výdajů.';
      }
      const age = this.scenario.currentAge.value;
      if (age < 0 || age > 120) {
        errors.currentAge = 'Zadejte platný věk (0–120).';
      }
      const retAge = this.scenario.retirementAge.value;
      if (this.mode === CalculationMode.byRetirementAge) {
        if (retAge < age) {
          errors.retirementAge =
            'Věk důchodu musí být vyšší nebo roven aktuálnímu věku.';
        } else if (retAge > 120) {
          errors.retirementAge = 'Zadejte platný věk (do 120).';
        }
      }
      if (
        this.mode === CalculationMode.byMonthlyContribution &&
        this.scenario.monthlyContribution.value <= 0
      ) {
        errors.monthlyContribution = 'Zadejte měsíční investici.';
      }
      return errors;
    },

    hasTargetField(): boolean {
      if (this.mode === CalculationMode.byRetirementAge) {
        return this.scenario.retirementAge.value > 0;
      }
      return this.scenario.monthlyContribution.value > 0;
    },

    visibleError(field: string, raw: string): string | null {
      if (!this.touched.has(field)) return null;
      return raw === '' ? null : raw;
    },

    recompute(): void {
      const errors = this.computeErrors();
      this.monthlyExpensesError = this.visibleError('monthlyExpenses', errors.monthlyExpenses);
      this.currentAgeError = this.visibleError('currentAge', errors.currentAge);
      this.retirementAgeError = this.visibleError('retirementAge', errors.retirementAge);
      this.monthlyContributionError = this.visibleError(
        'monthlyContribution',
        errors.monthlyContribution,
      );

      const anyErrors =
        errors.monthlyExpenses !== '' ||
        errors.currentAge !== '' ||
        errors.retirementAge !== '' ||
        errors.monthlyContribution !== '';

      if (anyErrors) {
        this.phase = 'error';
        this.planResult = null;
      } else if (
        this.scenario.monthlyExpenses.value > 0 &&
        this.scenario.currentAge.value > 0 &&
        this.hasTargetField()
      ) {
        this.phase = 'ready';
        this.planResult = new PlanService().runFullPlan(this.scenario, this.mode);
      } else {
        this.phase = 'initial';
        this.planResult = null;
      }
    },

    scheduleValidation(): void {
      if (this.validationTimer) clearTimeout(this.validationTimer);
      const wasReady = this.phase === 'ready';
      const wasError = this.phase === 'error';
      if (wasReady || wasError) {
        this.recompute();
      } else {
        this.validationTimer = setTimeout(() => {
          this.recompute();
          this.validationTimer = null;
        }, CALCULATOR_DEFAULT_VALIDATION_DELAY_MS);
      }
    },

    /** Add an event to the scenario and trigger recalculation. */
    addEvent(eventData: EventCreateData): void {
      const newEvents: (ScenarioEventType | ScenarioEventInitType)[] =
        [...this.scenario.events, eventData];
      this.scenario = this.scenario.copyWith({ events: newEvents });
      this.scheduleValidation();
    },

    /**
     * Replace an event by id with updated data, then trigger recalculation.
     * No op when the id is not found.
     */
    updateEvent(id: string, eventData: EventUpdateData): void {
      const idx = this.scenario.events.findIndex((e) => e.id === id);
      if (idx === -1) return;
      const existing = this.scenario.events[idx]!;
      const newEvents: (ScenarioEventType | ScenarioEventInitType)[] =
        [...this.scenario.events];

      const note = eventData.note ?? existing.note;
      const pensionType = eventData.pensionType ?? existing.pensionType;
      const growthRate = eventData.growthRate ?? existing.growthRate;

      newEvents[idx] = new ScenarioEvent({
        id: existing.id,
        kind: eventData.kind ?? existing.kind,
        label: eventData.label ?? existing.label,
        amount: eventData.amount ?? existing.amount.value,
        startAge: eventData.startAge ?? existing.startAge.value,
        frequency: eventData.frequency ?? existing.frequency,
        durationYears: eventData.durationYears ?? existing.durationYears,
        inflationAdjusted:
          eventData.inflationAdjusted ?? existing.inflationAdjusted,
        enabled: eventData.enabled ?? existing.enabled,
        ...(note !== undefined ? { note } : {}),
        ...(pensionType !== undefined ? { pensionType } : {}),
        ...(growthRate !== undefined ? { growthRate } : {}),
      });
      this.scenario = this.scenario.copyWith({ events: newEvents });
      this.scheduleValidation();
    },

    /**
     * Remove an event by id and trigger recalculation. No op when the id is not found.
     */
    removeEvent(id: string): void {
      const newEvents = this.scenario.events.filter((e) => e.id !== id);
      if (newEvents.length === this.scenario.events.length) return;
      this.scenario = this.scenario.copyWith({ events: newEvents });
      this.scheduleValidation();
    },

    /**
     * Duplicate an event with a fresh UUID and enabled=true, then trigger recalculation.
     * No op when the id is not found.
     */
    duplicateEvent(id: string): void {
      const source = this.scenario.events.find((e) => e.id === id);
      if (!source) return;
      const eventId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `evt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const duplicate = {
        id: eventId,
        kind: source.kind,
        label: source.label,
        amount: source.amount.value,
        startAge: source.startAge.value,
        frequency: source.frequency,
        durationYears: source.durationYears,
        inflationAdjusted: source.inflationAdjusted,
        enabled: true,
        ...(source.note != null ? { note: source.note } : {}),
        ...(source.pensionType != null ? { pensionType: source.pensionType } : {}),
        ...(source.growthRate != null ? { growthRate: source.growthRate } : {}),
      };
      const newEvents: (ScenarioEventType | ScenarioEventInitType)[] =
        [...this.scenario.events, duplicate];
      this.scenario = this.scenario.copyWith({ events: newEvents });
      this.scheduleValidation();
    },
  },
});